import { useState, useEffect, useMemo, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Search, Library, Filter, X, Zap, Shield, ClipboardList, Cloud,
  BookOpenCheck, ArrowUpRight, Loader2, Link2, MessageSquare, Save, Pencil,
} from 'lucide-react';
import MappingsManagerDialog from '../components/MappingsManagerDialog';

const PRIORITY_COLORS = {
  Critical: 'bg-red-100 text-red-800 border-red-200',
  High: 'bg-orange-100 text-orange-800 border-orange-200',
  Medium: 'bg-amber-100 text-amber-800 border-amber-200',
  Low: 'bg-slate-100 text-slate-700 border-slate-200',
};

const CLASSIFICATION_COLORS = {
  Preventive: 'bg-blue-50 text-blue-700 border-blue-200',
  Detective: 'bg-purple-50 text-purple-700 border-purple-200',
  Corrective: 'bg-teal-50 text-teal-700 border-teal-200',
};

const CONTROL_TYPE_COLORS = {
  Mandatory:   'bg-slate-900 text-white border-slate-900',
  Recommended: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  Excluded:    'bg-slate-100 text-slate-500 border-slate-200 line-through',
};

const STATUS_OPTIONS = ['Not Assessed', 'Non Compliant', 'Partially Compliant', 'Compliant', 'Not Applicable'];
const STATUS_TRIGGER_COLORS = {
  'Not Assessed':          'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200',
  'Non Compliant':         'bg-red-100 text-red-800 border-red-200 hover:bg-red-200',
  'Partially Compliant':   'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200',
  'Compliant':             'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200',
  'Not Applicable':        'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100',
};

function FrameworkBadge({ framework, codes }) {
  const label = framework?.short_name || framework?.id || 'Framework';
  const codesText = (codes || []).join(', ');
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={`framework-badge-${framework.id}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border cursor-help select-none"
            style={{
              borderColor: framework.color,
              color: framework.color,
              backgroundColor: `${framework.color}14`,
            }}
          >
            {label}
            <span className="ml-1 px-1.5 rounded-full bg-white/70 text-[10px] leading-4 font-semibold">
              {codes?.length || 0}
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-xs font-semibold mb-1">{framework.name}</p>
          <p className="text-xs">{codesText || 'No mapping codes'}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function DetailRow({ label, value, mono = false }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-[160px_1fr] gap-4 py-2 border-b border-slate-100 last:border-b-0">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-sm text-slate-800 whitespace-pre-line ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}

function EditableTitle({ control, canWrite, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setEditing(false); }, [control.control_id]);

  const save = async () => {
    if (!value.trim()) { toast.error('Title cannot be empty'); return; }
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/master-controls/${control.control_id}/content`, { name: value });
      toast.success('Title updated for all tenants');
      onSaved?.(res.data);
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save title');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <span className="flex items-center gap-2 flex-1">
        <Input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          className="h-9 text-base"
          data-testid="mcl-title-input"
        />
        <Button size="sm" onClick={save} disabled={saving} data-testid="mcl-title-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving} data-testid="mcl-title-cancel-btn">
          <X className="w-4 h-4" />
        </Button>
      </span>
    );
  }
  return (
    <span className="flex items-center gap-2 group">
      <span className="text-slate-900" data-testid="mcl-title-text">{control.name}</span>
      {canWrite && (
        <button
          type="button"
          onClick={() => { setValue(control.name || ''); setEditing(true); }}
          className="p-1 rounded text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"
          title="Edit title (applies to all tenants)"
          data-testid="mcl-title-edit-btn"
        >
          <Pencil className="w-4 h-4" />
        </button>
      )}
    </span>
  );
}

function GapRecommendationSection({ control, canWrite, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ gap_description: '', recommendation: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { setEditing(false); }, [control.control_id]);

  const startEdit = () => {
    setDraft({ gap_description: control.gap_description || '', recommendation: control.recommendation || '' });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/master-controls/${control.control_id}/content`, draft);
      toast.success('Updated for all tenants');
      onSaved?.(res.data);
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-4 mb-2" data-testid="mcl-gap-rec-section">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <ArrowUpRight className="w-4 h-4 text-slate-500" /> Gap & Recommendation
        </h3>
        {canWrite && !editing && (
          <Button variant="outline" size="sm" onClick={startEdit} data-testid="mcl-gap-rec-edit-btn">
            <Pencil className="w-4 h-4 mr-1" /> Edit
          </Button>
        )}
      </div>
      {editing ? (
        <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            Changes apply to every tenant. Tenants with their own custom text in Gap Assessment keep it.
          </p>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gap Description</label>
            <Textarea rows={4} className="mt-1" value={draft.gap_description}
              onChange={(e) => setDraft({ ...draft, gap_description: e.target.value })}
              data-testid="mcl-gap-description-input" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recommendation</label>
            <Textarea rows={5} className="mt-1" value={draft.recommendation}
              onChange={(e) => setDraft({ ...draft, recommendation: e.target.value })}
              data-testid="mcl-recommendation-input" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={saving} data-testid="mcl-gap-rec-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="mcl-gap-rec-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save for all tenants
            </Button>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white p-1">
          <DetailRow label="Gap Description" value={control.gap_description} />
          <DetailRow label="Recommendation" value={control.recommendation} />
          <DetailRow label="Priority" value={control.priority} />
          <DetailRow label="Effort" value={control.effort} />
        </div>
      )}
    </section>
  );
}

function ControlDetailDialog({ open, onOpenChange, control, frameworksById, canWrite, assessment, onMappingsChanged, onAssessmentSaved, onControlUpdated }) {
  const [mappingsOpen, setMappingsOpen] = useState(false);
  const [comments, setComments] = useState('');
  const [savingComments, setSavingComments] = useState(false);

  useEffect(() => {
    setComments(assessment?.notes || '');
  }, [assessment?.notes, control?.control_id]);

  if (!control) return null;
  const mappingsList = control.mappings || [];

  const saveComments = async () => {
    setSavingComments(true);
    try {
      await axios.put(`${API}/master-controls/${control.control_id}/assessment`, {
        notes: comments || null,
      });
      toast.success('Comments saved');
      onAssessmentSaved?.(control.control_id, { notes: comments });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save comments');
    } finally {
      setSavingComments(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="mcl-detail-dialog"
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="font-mono text-slate-500 text-base">{control.control_id}</span>
            <EditableTitle control={control} canWrite={canWrite} onSaved={onControlUpdated} />
          </DialogTitle>
          <DialogDescription className="mt-2">
            <span className="text-slate-600">{control.domain}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 pt-2">
          {control.priority && (
            <Badge className={`${PRIORITY_COLORS[control.priority] || ''} border`}>{control.priority}</Badge>
          )}
          {control.classification && (
            <Badge className={`${CLASSIFICATION_COLORS[control.classification] || ''} border`}>
              {control.classification}
            </Badge>
          )}
          {control.effort && (
            <Badge variant="outline">Effort: {control.effort}</Badge>
          )}
          {control.frequency && (
            <Badge variant="outline">Freq: {control.frequency}</Badge>
          )}
          {control.quick_win && (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border">
              <Zap className="w-3 h-3 mr-1" /> Quick Win
            </Badge>
          )}
        </div>

        <section className="mt-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <BookOpenCheck className="w-4 h-4 text-slate-500" /> Control
          </h3>
          <div className="rounded-lg border border-slate-200 bg-white p-1">
            <DetailRow label="Description" value={control.description} />
            <DetailRow label="Owner" value={control.owner} />
            <DetailRow label="Frequency" value={control.frequency} />
            <DetailRow label="Notes" value={control.notes} />
          </div>
        </section>

        <section className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Shield className="w-4 h-4 text-slate-500" /> Framework Mappings
            </h3>
            {canWrite && (
              <Button
                variant="outline" size="sm"
                onClick={() => setMappingsOpen(true)}
                data-testid="mcl-manage-mappings-btn"
              >
                <Link2 className="w-4 h-4 mr-1" /> Manage mappings
              </Button>
            )}
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
            {mappingsList.length === 0 ? (
              <p className="text-sm text-slate-500 italic">No framework mappings.</p>
            ) : (
              mappingsList.map((m) => {
                const fw = frameworksById[m.framework_id] || {
                  id: m.framework_id, name: m.framework_id, color: '#64748b',
                };
                return (
                  <div key={m.framework_id} className="flex items-start gap-3">
                    <FrameworkBadge framework={fw} codes={m.codes} />
                    <span className="text-sm text-slate-700 font-mono">{(m.codes || []).join(', ')}</span>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="mt-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <Cloud className="w-4 h-4 text-slate-500" /> Implementation Guidance
          </h3>
          <div className="rounded-lg border border-slate-200 bg-white p-1">
            <DetailRow label="Microsoft 365" value={control.m365_guidance} />
            <DetailRow label="Azure" value={control.azure_guidance} />
          </div>
        </section>

        <section className="mt-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-slate-500" /> Evidence
          </h3>
          <div className="rounded-lg border border-slate-200 bg-white p-1">
            <DetailRow label="Evidence Required" value={control.evidence_required} />
            <DetailRow label="Evidence Examples" value={control.evidence_examples} />
          </div>
        </section>

        <GapRecommendationSection control={control} canWrite={canWrite} onSaved={onControlUpdated} />

        <section className="mt-4 mb-2">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-slate-500" /> Comments
            </h3>
            <span className="text-xs text-slate-400">Tenant-scoped</span>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
            <Textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Notes, context, decisions, evidence pointers… visible only to your tenant."
              rows={4}
              disabled={!canWrite || savingComments}
              data-testid="mcl-comments-textarea"
            />
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={saveComments}
                disabled={!canWrite || savingComments || comments === (assessment?.notes || '')}
                data-testid="mcl-comments-save-btn"
              >
                {savingComments ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save comments
              </Button>
            </div>
          </div>
        </section>
      </DialogContent>
      {mappingsOpen && (
        <MappingsManagerDialog
          open={mappingsOpen}
          onOpenChange={setMappingsOpen}
          mode="control"
          masterControl={control}
          onChanged={onMappingsChanged}
        />
      )}
    </Dialog>
  );
}

export default function MasterControlLibraryPage() {
  const { user, viewingOrg } = useContext(AuthContext);
  const canWrite = user?.role === 'admin' || user?.role === 'super_admin';
  const [loading, setLoading] = useState(true);
  const [controls, setControls] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({
    domains: [], priorities: [], classifications: [], frameworks: [], total_controls: 0,
  });
  const [search, setSearch] = useState('');
  const [domain, setDomain] = useState('all');
  const [priority, setPriority] = useState('all');
  const [classification, setClassification] = useState('all');
  const [controlType, setControlType] = useState('all');
  const [framework, setFramework] = useState('all');
  const [quickWin, setQuickWin] = useState('all');
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assessments, setAssessments] = useState({});
  const [savingStatus, setSavingStatus] = useState(null);
  const [savingType, setSavingType] = useState(null);
  const [typeSummary, setTypeSummary] = useState(null);

  const frameworksById = useMemo(() => {
    const map = {};
    (facets.frameworks || []).forEach((fw) => { map[fw.id] = fw; });
    return map;
  }, [facets.frameworks]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/master-controls/facets`);
        setFacets(res.data);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load filter options');
      }
    })();
  }, []);

  const loadAssessments = async () => {
    try {
      const res = await axios.get(`${API}/master-controls/assessments/all`);
      setAssessments(res.data.assessments || {});
    } catch (e) {
      console.error(e);
    }
  };

  const loadTypeSummary = async () => {
    try {
      const res = await axios.get(`${API}/master-controls/summary`);
      setTypeSummary(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { loadAssessments(); loadTypeSummary(); }, []);
  useEffect(() => { loadAssessments(); loadTypeSummary(); }, [viewingOrg]);

  const updateStatus = async (controlId, newStatus) => {
    setSavingStatus(controlId);
    // Optimistic update
    const prev = assessments[controlId];
    setAssessments((a) => ({
      ...a,
      [controlId]: { ...(prev || { applicability: 'Applicable' }), status: newStatus },
    }));
    try {
      await axios.put(`${API}/master-controls/${controlId}/assessment`, { status: newStatus });
      toast.success(`${controlId} → ${newStatus}`);
      loadTypeSummary();
    } catch (e) {
      // Rollback
      setAssessments((a) => ({ ...a, [controlId]: prev }));
      toast.error(e.response?.data?.detail || 'Failed to update status');
    } finally {
      setSavingStatus(null);
    }
  };

  const updateControlType = async (controlId, newType) => {
    setSavingType(controlId);
    // Optimistic update on the row
    const prev = controls.find((c) => c.control_id === controlId)?.control_type;
    setControls((rows) => rows.map((c) => (c.control_id === controlId ? { ...c, control_type: newType } : c)));
    try {
      await axios.patch(`${API}/master-controls/${controlId}/control-type`, null, { params: { control_type: newType } });
      toast.success(`${controlId} type → ${newType}`);
      loadTypeSummary();
    } catch (e) {
      setControls((rows) => rows.map((c) => (c.control_id === controlId ? { ...c, control_type: prev } : c)));
      toast.error(e.response?.data?.detail || 'Failed to update control type');
    } finally {
      setSavingType(null);
    }
  };

  useEffect(() => {
    let alive = true;
    const params = new URLSearchParams();
    if (search.trim()) params.set('search', search.trim());
    if (domain !== 'all') params.set('domain', domain);
    if (priority !== 'all') params.set('priority', priority);
    if (classification !== 'all') params.set('classification', classification);
    if (controlType !== 'all') params.set('control_type', controlType);
    if (framework !== 'all') params.set('framework', framework);
    if (quickWin !== 'all') params.set('quick_win', quickWin === 'yes' ? 'true' : 'false');
    setLoading(true);
    axios.get(`${API}/master-controls?${params.toString()}`)
      .then((res) => {
        if (!alive) return;
        setControls(res.data.items || []);
        setTotal(res.data.total || 0);
      })
      .catch(() => alive && toast.error('Failed to load master controls'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [search, domain, priority, classification, controlType, framework, quickWin]);

  const clearFilters = () => {
    setSearch('');
    setDomain('all');
    setPriority('all');
    setClassification('all');
    setControlType('all');
    setFramework('all');
    setQuickWin('all');
  };

  const openDetail = async (controlId) => {
    try {
      const res = await axios.get(`${API}/master-controls/${controlId}`);
      setSelected(res.data);
      setDialogOpen(true);
    } catch {
      toast.error('Failed to load control details');
    }
  };

  const activeFilterCount = [
    search.trim() && 1, domain !== 'all' && 1, priority !== 'all' && 1,
    classification !== 'all' && 1, controlType !== 'all' && 1,
    framework !== 'all' && 1, quickWin !== 'all' && 1,
  ].filter(Boolean).length;

  return (
    <div className="space-y-6" data-testid="master-control-library-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Library className="w-4 h-4" />
            <span>Compliance Engine v2</span>
            <span className="text-slate-300">/</span>
            <span>Master Control Library</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1">Master Control Library</h1>
          <p className="text-sm text-slate-600 mt-1">
            One canonical library of controls. Assess once, map to many frameworks,
            reuse evidence, generate everything.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Card className="border-slate-200">
            <CardContent className="py-3 px-4 flex items-center gap-3">
              <div className="text-2xl font-bold text-slate-900" data-testid="mcl-total-count">
                {facets.total_controls || 0}
              </div>
              <div className="text-xs text-slate-500 leading-tight">Controls<br/>in library</div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 hidden sm:block">
            <CardContent className="py-3 px-4 flex items-center gap-3">
              <div className="text-2xl font-bold text-slate-900" data-testid="mcl-framework-count">
                {facets.frameworks?.length || 0}
              </div>
              <div className="text-xs text-slate-500 leading-tight">Frameworks<br/>mapped</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Mandatory / Recommended / Excluded metrics */}
      {typeSummary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="mcl-type-metrics">
          <Card className="border-slate-900/20">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide font-semibold text-slate-900 mb-2">Mandatory</div>
              <div className="text-2xl font-bold text-slate-900" data-testid="mcl-mandatory-total">{typeSummary.by_control_type?.Mandatory?.total ?? 0}</div>
              <div className="text-xs text-slate-500">total controls · {typeSummary.by_control_type?.Mandatory?.completion_percentage ?? 0}% complete</div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-slate-500">Compliant</span> <span className="font-semibold text-emerald-700 ml-1" data-testid="mcl-mandatory-implemented">{typeSummary.by_control_type?.Mandatory?.Compliant ?? 0}</span></div>
                <div><span className="text-slate-500">Partially Compliant</span> <span className="font-semibold text-blue-700 ml-1">{typeSummary.by_control_type?.Mandatory?.['Partially Compliant'] ?? 0}</span></div>
                <div><span className="text-slate-500">Non Compliant</span> <span className="font-semibold text-red-700 ml-1">{typeSummary.by_control_type?.Mandatory?.['Non Compliant'] ?? 0}</span></div>
                <div><span className="text-slate-500">Not Applicable</span> <span className="font-semibold text-slate-500 ml-1" data-testid="mcl-mandatory-not-applicable">{typeSummary.by_control_type?.Mandatory?.['Not Applicable'] ?? 0}</span></div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-indigo-200 bg-indigo-50/40">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide font-semibold text-indigo-800 mb-2">Recommended</div>
              <div className="text-2xl font-bold text-indigo-900" data-testid="mcl-recommended-total">{typeSummary.by_control_type?.Recommended?.total ?? 0}</div>
              <div className="text-xs text-indigo-700">tracked separately · {typeSummary.by_control_type?.Recommended?.completion_percentage ?? 0}% complete</div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-indigo-700/70">Compliant</span> <span className="font-semibold text-emerald-700 ml-1" data-testid="mcl-recommended-implemented">{typeSummary.by_control_type?.Recommended?.Compliant ?? 0}</span></div>
                <div><span className="text-indigo-700/70">Partially Compliant</span> <span className="font-semibold text-blue-700 ml-1">{typeSummary.by_control_type?.Recommended?.['Partially Compliant'] ?? 0}</span></div>
                <div><span className="text-indigo-700/70">Non Compliant</span> <span className="font-semibold text-red-700 ml-1">{typeSummary.by_control_type?.Recommended?.['Non Compliant'] ?? 0}</span></div>
                <div><span className="text-indigo-700/70">Not Applicable</span> <span className="font-semibold text-indigo-900/60 ml-1" data-testid="mcl-recommended-not-applicable">{typeSummary.by_control_type?.Recommended?.['Not Applicable'] ?? 0}</span></div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-100/50">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide font-semibold text-slate-600 mb-2">Excluded</div>
              <div className="text-2xl font-bold text-slate-500 line-through" data-testid="mcl-excluded-total">{typeSummary.by_control_type?.Excluded?.total ?? 0}</div>
              <div className="text-xs text-slate-500">out of scope — does not count toward compliance</div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-500">
                <div>Implemented <span className="font-semibold text-slate-700 ml-1" data-testid="mcl-excluded-implemented">{typeSummary.by_control_type?.Excluded?.Implemented ?? 0}</span></div>
                <div>Not Applicable <span className="font-semibold text-slate-700 ml-1" data-testid="mcl-excluded-not-applicable">{typeSummary.by_control_type?.Excluded?.['Not Applicable'] ?? 0}</span></div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="mcl-search-input"
                placeholder="Search by Control ID, name, description or domain..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 flex-shrink-0">
              <Select value={domain} onValueChange={setDomain}>
                <SelectTrigger data-testid="mcl-filter-domain" className="min-w-[140px]"><SelectValue placeholder="Domain" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All domains</SelectItem>
                  {facets.domains.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="mcl-filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All priorities</SelectItem>
                  {facets.priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={controlType} onValueChange={setControlType}>
                <SelectTrigger data-testid="mcl-filter-control-type"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="mcl-filter-control-type-all">All types</SelectItem>
                  <SelectItem value="Mandatory" data-testid="mcl-filter-control-type-Mandatory">Mandatory</SelectItem>
                  <SelectItem value="Recommended" data-testid="mcl-filter-control-type-Recommended">Recommended</SelectItem>
                  <SelectItem value="Excluded" data-testid="mcl-filter-control-type-Excluded">Excluded</SelectItem>
                </SelectContent>
              </Select>
              <Select value={framework} onValueChange={setFramework}>
                <SelectTrigger data-testid="mcl-filter-framework"><SelectValue placeholder="Framework" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All frameworks</SelectItem>
                  {facets.frameworks.map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.short_name || f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={quickWin} onValueChange={setQuickWin}>
                <SelectTrigger data-testid="mcl-filter-quickwin"><SelectValue placeholder="Quick win?" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  <SelectItem value="yes">Quick wins only</SelectItem>
                  <SelectItem value="no">Non quick-wins</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <Filter className="w-3.5 h-3.5" />
              Showing <span className="font-semibold text-slate-800" data-testid="mcl-result-count">{total}</span>
              {' '}of {facets.total_controls || 0} controls
              {activeFilterCount > 0 && <span>· {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active</span>}
            </div>
            {activeFilterCount > 0 && (
              <Button
                variant="ghost" size="sm"
                onClick={clearFilters}
                data-testid="mcl-clear-filters"
                className="text-slate-600"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[90px]">ID</TableHead>
                  <TableHead className="min-w-[280px]">Control</TableHead>
                  <TableHead>Domain</TableHead>
                  <TableHead className="min-w-[110px]">Type</TableHead>
                  <TableHead className="min-w-[180px]">Status</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead className="min-w-[220px]">Frameworks</TableHead>
                  <TableHead className="text-center">QW</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading controls...
                    </TableCell>
                  </TableRow>
                ) : controls.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      No controls match the current filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  controls.map((c) => (
                    <TableRow
                      key={c.control_id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => openDetail(c.control_id)}
                      data-testid={`mcl-row-${c.control_id}`}
                    >
                      <TableCell className="font-mono text-slate-600">{c.control_id}</TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900">{c.name}</div>
                        <div className="text-xs text-slate-500 line-clamp-1">{c.description}</div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-700">{c.domain}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const cur = c.control_type || 'Mandatory';
                          const trigCls = CONTROL_TYPE_COLORS[cur] || CONTROL_TYPE_COLORS.Mandatory;
                          return (
                            <Select
                              value={cur}
                              onValueChange={(v) => updateControlType(c.control_id, v)}
                              disabled={!canWrite || savingType === c.control_id}
                            >
                              <SelectTrigger
                                data-testid={`mcl-control-type-${c.control_id}`}
                                className={`h-7 text-xs font-medium border ${trigCls} px-2`}
                              >
                                {savingType === c.control_id ? (
                                  <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>
                                ) : (
                                  <SelectValue />
                                )}
                              </SelectTrigger>
                              <SelectContent>
                                {['Mandatory', 'Recommended', 'Excluded'].map((t) => (
                                  <SelectItem
                                    key={t}
                                    value={t}
                                    data-testid={`mcl-control-type-option-${c.control_id}-${t}`}
                                  >
                                    {t}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          );
                        })()}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const cur = (assessments[c.control_id]?.status) || 'Not Assessed';
                          const trigCls = STATUS_TRIGGER_COLORS[cur] || STATUS_TRIGGER_COLORS['Not Assessed'];
                          return (
                            <Select
                              value={cur}
                              onValueChange={(v) => updateStatus(c.control_id, v)}
                              disabled={!canWrite || savingStatus === c.control_id}
                            >
                              <SelectTrigger
                                data-testid={`mcl-status-select-${c.control_id}`}
                                className={`h-7 text-xs font-medium border ${trigCls} px-2`}
                              >
                                {savingStatus === c.control_id ? (
                                  <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>
                                ) : (
                                  <SelectValue />
                                )}
                              </SelectTrigger>
                              <SelectContent>
                                {STATUS_OPTIONS.map((s) => (
                                  <SelectItem key={s} value={s} data-testid={`mcl-status-option-${c.control_id}-${s.replace(/\s+/g, '-')}`}>
                                    {s}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {c.priority && (
                          <Badge className={`${PRIORITY_COLORS[c.priority] || ''} border text-xs`}>
                            {c.priority}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(c.mappings || []).map((m) => {
                            const fw = frameworksById[m.framework_id] || {
                              id: m.framework_id, name: m.framework_id, color: '#64748b',
                            };
                            return <FrameworkBadge key={m.framework_id} framework={fw} codes={m.codes} />;
                          })}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {c.quick_win && (
                          <Zap className="w-4 h-4 text-emerald-600 inline" data-testid={`mcl-qw-${c.control_id}`} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <ControlDetailDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        control={selected}
        frameworksById={frameworksById}
        canWrite={canWrite}
        assessment={selected ? assessments[selected.control_id] : null}
        onAssessmentSaved={(cid, patch) => {
          setAssessments((a) => ({ ...a, [cid]: { ...(a[cid] || {}), ...patch } }));
        }}
        onControlUpdated={(fresh) => {
          setSelected((prev) => ({ ...prev, ...fresh }));
          setControls((cs) => cs.map((c) => (c.control_id === fresh.control_id ? { ...c, ...fresh } : c)));
        }}
        onMappingsChanged={async () => {
          if (selected?.control_id) {
            const res = await axios.get(`${API}/master-controls/${selected.control_id}`);
            setSelected(res.data);
          }
        }}
      />
    </div>
  );
}
